import { create } from 'zustand';
import { recentRecordsOf, withVisit, type RecentRecord } from '@nest-recruit/shared';
import { RECENT_RECORDS_KEY, createUserPreferenceStore } from '../lib/preferences';
import { useSession } from './session';

// 06-qol §1 and DECISIONS-LOG S29–S32 Q29: the last ten records the person opened, for the
// palette's Recent section and the avatar menu. Kept as a preference, so it follows them.

function store() {
  return createUserPreferenceStore(useSession.getState().session?.id ?? 'anonymous');
}

interface RecentsState {
  readonly records: readonly RecentRecord[];
  /** Re-read after sign-in, when the server's copy has landed. */
  load(): void;
  visit(record: Omit<RecentRecord, 'viewedAt'>): void;
  /** A record the person can no longer open drops out (Q29). */
  forget(id: string): void;
}

export const useRecents = create<RecentsState>((set, get) => ({
  records: [],
  load: () => set({ records: recentRecordsOf(store().get<unknown>(RECENT_RECORDS_KEY, [])) }),
  visit: (record) => {
    const current = get().records;
    const latest = current[0];
    // The same record opened again, with nothing new to say, is not a change worth a write.
    if (latest && latest.id === record.id && latest.title === record.title && latest.subtitle === record.subtitle) return;
    const records = withVisit(current, { ...record, viewedAt: new Date().toISOString() });
    store().set(RECENT_RECORDS_KEY, records);
    set({ records });
  },
  forget: (id) => {
    const current = get().records;
    if (!current.some((entry) => entry.id === id)) return;
    const records = current.filter((entry) => entry.id !== id);
    store().set(RECENT_RECORDS_KEY, records);
    set({ records });
  },
}));
